import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { ApiError, type InstanceSummary } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { Icon } from "../components/Icon";

const token = () => getAccessToken() ?? "";

/** Modal to add more of the user's existing dials to a group (those not already in it), grouped by definition. */
export function AddMembersModal({
  groupId,
  existing,
  onClose,
  onAdded,
}: {
  groupId: string;
  existing: ReadonlySet<string>;
  onClose: () => void;
  onAdded: () => void | Promise<void>;
}) {
  const [instances, setInstances] = useState<InstanceSummary[] | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const portfolio = await api.listMyInstances(token());
        if (!cancelled) setInstances(portfolio.instances.filter((i) => !existing.has(i.id)));
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Could not load your dials.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existing]);

  const grouped = useMemo(() => {
    const rows = instances ?? [];
    const q = search.trim().toLowerCase();
    const filtered = q ? rows.filter((i) => `${i.name} ${i.organisationName} ${i.definitionName}`.toLowerCase().includes(q)) : rows;
    const byDef = new Map<string, InstanceSummary[]>();
    for (const i of filtered) {
      const l = byDef.get(i.definitionName) ?? [];
      l.push(i);
      byDef.set(i.definitionName, l);
    }
    return [...byDef.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [instances, search]);

  const toggle = (id: string) =>
    setSelected((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function add() {
    if (selected.size === 0) {
      onClose();
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      for (const id of selected) {
        await api.addGroupMember(token(), groupId, id);
      }
      await onAdded();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not add the dials.");
      setBusy(false);
    }
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="kd-card" style={{ width: 560, maxWidth: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "18px 20px", borderBottom: "var(--border-w) solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: "var(--text)" }}>Add dials</h2>
          <button onClick={onClose} className="kd-focusable" style={iconBtn} aria-label="Close">
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12, overflow: "auto" }}>
          <div style={{ position: "relative" }}>
            <Icon name="search" size={15} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text-faint)" }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter your dials…" style={{ ...field, width: "100%", paddingLeft: 34 }} />
          </div>

          <div style={{ fontSize: 12.5, color: "var(--text-muted)", fontWeight: 600 }}>{selected.size} selected</div>

          <div style={{ border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
            {instances === null && <div style={{ padding: 18, textAlign: "center", color: "var(--text-faint)", fontSize: 13.5 }}>Loading…</div>}
            {instances !== null && grouped.length === 0 && (
              <div style={{ padding: 18, textAlign: "center", color: "var(--text-faint)", fontSize: 13.5 }}>No more dials to add.</div>
            )}
            {grouped.map(([def, rows]) => (
              <div key={def}>
                <div style={{ padding: "7px 12px", background: "var(--surface-2)", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-muted)" }}>{def}</div>
                {rows.map((i) => (
                  <label key={i.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderTop: "var(--border-w) solid var(--border)", cursor: "pointer" }}>
                    <input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.name}</span>
                      <span style={{ display: "block", fontSize: 12, color: "var(--text-faint)" }}>{i.organisationName}</span>
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>

          {err && <p style={{ margin: 0, color: "var(--st-overdue)", fontSize: 13 }}>{err}</p>}
        </div>

        <div style={{ padding: "14px 20px", borderTop: "var(--border-w) solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} disabled={busy} className="kd-focusable" style={btnGhost}>Cancel</button>
          <button onClick={add} disabled={busy || selected.size === 0} className="kd-focusable" style={btnPrimary}>
            <Icon name="plus" size={15} /> Add {selected.size > 0 ? selected.size : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

const field: CSSProperties = { padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)", font: "inherit", fontSize: 14 };
const iconBtn: CSSProperties = { display: "inline-flex", padding: 6, borderRadius: "var(--radius-sm)", border: "none", background: "none", color: "var(--text-muted)", cursor: "pointer" };
const btnGhost: CSSProperties = { padding: "9px 14px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)", font: "inherit", fontWeight: 600, fontSize: 13.5, cursor: "pointer" };
const btnPrimary: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: "var(--radius-sm)", border: "none", background: "var(--brand-600, #4338ca)", color: "#fff", font: "inherit", fontWeight: 700, fontSize: 13.5, cursor: "pointer" };
